/**
 * MyAdvisorReferrals Component
 * Shows the list of advisors referred by the current user
 */
import React, { useState, useEffect } from 'react';
import { FaUserTie, FaSpinner, FaPhone, FaEnvelope, FaClock, FaCheckCircle, FaTimesCircle, FaUsers, FaRupeeSign } from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyAdvisorReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/advisor/my-advisor-referrals`, { headers: headers() });
      setReferrals(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: FaClock, label: 'Pending' },
      contacted: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: FaPhone, label: 'Contacted' },
      registered: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: FaUsers, label: 'Registered' },
      onboarded: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: FaCheckCircle, label: 'Onboarded' },
      rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: FaTimesCircle, label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon size={10} />
        <span>{config.label}</span>
      </span>
    );
  };

  const getRewardBadge = (status, rewardStatus) => {
    if (status !== 'onboarded') return null;
    
    if (rewardStatus === 'eligible' || rewardStatus === 'paid') {
      return (
        <span className="inline-flex items-center space-x-1 px-2 py-1 bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/30 rounded-full text-xs font-medium">
          <FaRupeeSign size={10} />
          <span>1,500/year</span>
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] p-6">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  if (referrals.length === 0) {
    return null;
  }

  return (
    <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] overflow-hidden" data-testid="my-advisor-referrals">
      {/* Header */}
      <div className="p-4 border-b border-[#2D5A8F] bg-[#0F172A]/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FaUserTie className="text-[#FFD700]" />
            <h3 className="text-lg font-bold text-white">My Advisor Referrals</h3>
            <span className="px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700] text-xs font-medium rounded-full">
              {referrals.length}
            </span>
          </div>
        </div>
      </div>

      {/* Referrals List */}
      <div className="divide-y divide-[#2D5A8F]/50">
        {referrals.map((referral) => (
          <div key={referral.id} className="p-4 hover:bg-[#0F172A]/30 transition-all">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-semibold text-white">{referral.referred_advisor_name}</h4>
                  {getStatusBadge(referral.status)}
                  {getRewardBadge(referral.status, referral.reward_status)}
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                  <span className="flex items-center space-x-1">
                    <FaPhone size={12} />
                    <span>{referral.referred_advisor_contact}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <FaEnvelope size={12} />
                    <span>{referral.referred_advisor_email}</span>
                  </span>
                </div>
                {referral.referred_advisor_profile && (
                  <p className="text-gray-500 text-sm mt-2 line-clamp-2">
                    {referral.referred_advisor_profile}
                  </p>
                )}
              </div>
              <div className="text-right text-xs text-gray-500">
                {new Date(referral.created_at).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyAdvisorReferrals;
